import React, { useEffect, useState } from 'react';
import { api } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Loader2, Star, ShieldCheck } from 'lucide-react';

const DayChips = ({ days }) => (
  <span className="text-[10px] text-[#8E85A0]">{(days || []).map(d => d[0].toUpperCase() + d.slice(1, 3)).join(' · ') || '—'}</span>
);

export default function SetCaseloadDialog({ participant, lookback, current, onClose, onSaved }) {
  const [cands, setCands] = useState(null);
  const [primary, setPrimary] = useState(current?.primary_id || '');
  const [alts, setAlts] = useState((current?.alternates || []).map(a => a.id));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/caseloads/candidates', { params: { participant, lookback_days: lookback } });
        setCands(data);
      } catch { toast.error('Failed to load candidates'); }
    })();
  }, [participant, lookback]);

  const toggleAlt = (id) => setAlts(a => a.includes(id) ? a.filter(x => x !== id) : (a.length < 3 ? [...a, id] : a));

  const save = async (clear = false) => {
    setSaving(true);
    try {
      await api.put('/caseloads/assign', clear
        ? { participant, primary_id: '' }
        : { participant, primary_id: primary, alternate_ids: alts.filter(x => x !== primary) });
      toast.success(clear ? 'Caseload cleared' : 'Caseload saved');
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="set-caseload-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#2D1B4E]">Set caseload — {participant}</DialogTitle>
        </DialogHeader>
        {!cands ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#6B3FA0]" /></div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-[#8E85A0]">
              {participant} is typically here <b>{cands.attend_days.map(d => d[0].toUpperCase() + d.slice(1, 3)).join(' · ') || '(no pattern yet)'}</b>.
              Candidates ranked by availability match × history on those days × familiarity × capacity. Pick one <b>primary</b> (★) and up to 3 <b>alternates</b> (✓).
            </p>
            <div className="max-h-[50vh] overflow-y-auto space-y-1.5 pr-1">
              {cands.candidates.map(c => (
                <div key={c.id}
                     className={`rounded-lg border px-3 py-2 flex items-center gap-2 flex-wrap ${primary === c.id ? 'border-[#6B3FA0] bg-[#F7F2FD]' : alts.includes(c.id) ? 'border-[#9B7ED8] bg-[#FBF9FE]' : 'border-[#E8E2F2] bg-white'}`}
                     data-testid={`caseload-cand-${c.id}`}>
                  <button onClick={() => setPrimary(primary === c.id ? '' : c.id)} title="Set as primary"
                          className={`h-7 w-7 rounded-full flex items-center justify-center border ${primary === c.id ? 'bg-[#6B3FA0] text-white border-[#6B3FA0]' : 'text-[#9B7ED8] border-[#DED8EB] hover:border-[#6B3FA0]'}`}
                          data-testid={`cand-primary-${c.id}`}>
                    <Star className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => toggleAlt(c.id)} title="Toggle alternate"
                          className={`h-7 w-7 rounded-full flex items-center justify-center border text-xs font-black ${alts.includes(c.id) ? 'bg-[#9B7ED8] text-white border-[#9B7ED8]' : 'text-[#9B7ED8] border-[#DED8EB] hover:border-[#9B7ED8]'}`}
                          data-testid={`cand-alt-${c.id}`}>✓</button>
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-[#2D1B4E]">{c.name}</div>
                    <DayChips days={c.available_days} />
                  </div>
                  <div className="ml-auto flex items-center gap-2 text-[11px] font-semibold">
                    <span className={`rounded-full px-2 py-0.5 ${c.avail_match >= 100 ? 'bg-emerald-50 text-emerald-700' : c.avail_match >= 50 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-600'}`}>
                      {c.avail_match}% avail match
                    </span>
                    {c.familiarity > 0 && <span className="bg-[#F0EAFA] text-[#6B3FA0] rounded-full px-2 py-0.5">knows them {c.familiarity}×</span>}
                    {c.avg_weekly_hours != null && <span className="text-[#8E85A0]">{c.avg_weekly_hours}h/wk</span>}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2 justify-between">
              {current && (
                <Button variant="outline" onClick={() => save(true)} disabled={saving} className="text-red-600 border-red-200" data-testid="caseload-clear-btn">
                  Clear caseload
                </Button>
              )}
              <Button onClick={() => save(false)} disabled={saving || !primary} className="ml-auto bg-[#2D1B4E] hover:bg-[#3D2A63] text-white gap-1.5" data-testid="caseload-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Save caseload
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
